import {
  Notification,
  NotificationColor,
  NotificationStatus,
} from "@shared/types";
import {
  AGGREGATE_NOTIFICATION_COMPONENT_TO_BUTTON_TEXT,
  APP_CLICKABLE_SCREENS,
  WEB_CLICKABLE_SCREENS,
} from "@shared/features/notifications/constants";
import { useEffect, useState } from "react";
import { sharedLib } from "@shared/services/shared.service";
import { useTheme } from "styled-components";
import { addTrailingDots } from "@shared/features/notifications/utils";

const useNotification = (
  groupedNotification: Notification[],
  maxNotificationLength: number,
  expandedTimeline: boolean,
  expandedSingleNotification: boolean,
  timeoutMs?: number,
  isApp?: boolean
) => {
  const theme = useTheme();
  const [delayedExpandedTimeline, setDelayedExpandedTimeline] = useState(false);

  const [markNotificationsRead] =
    sharedLib.sharedApi.notifications.useMarkNotificationsReadMutation();

  const isAggregateNotification = groupedNotification?.length > 1;
  const lastNotificationItem = groupedNotification?.[0];
  const showBadge = groupedNotification?.some(
    (subItem) => subItem.status === NotificationStatus.UNREAD
  );

  const isLongNotification =
    !!lastNotificationItem?.sub_message &&
    lastNotificationItem.sub_message.length > maxNotificationLength;

  const isCollapsible = isAggregateNotification || isLongNotification;

  const clickable =
    (isApp ? APP_CLICKABLE_SCREENS : WEB_CLICKABLE_SCREENS).has(
      lastNotificationItem?.screen
    ) || isCollapsible;

  const showButton =
    (!isCollapsible &&
      lastNotificationItem?.button_text &&
      (lastNotificationItem.button_link || !lastNotificationItem.screen)) ||
    (isAggregateNotification && expandedTimeline) ||
    (isLongNotification && expandedSingleNotification);

  const groupedNotificationButtonText =
    AGGREGATE_NOTIFICATION_COMPONENT_TO_BUTTON_TEXT[
      lastNotificationItem?.component_type
    ];
  const buttonText =
    lastNotificationItem?.button_text ||
    (groupedNotificationButtonText
      ? `Show ${groupedNotificationButtonText}`
      : undefined);

  useEffect(() => {
    if (expandedTimeline) setDelayedExpandedTimeline(expandedTimeline);
    else {
      const ms = Math.max(
        200,
        (groupedNotification.length - 1) * (timeoutMs ?? 50)
      );
      const timeout = setTimeout(
        () => setDelayedExpandedTimeline(expandedTimeline),
        ms
      );
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [expandedTimeline]);

  const markUnreadNotificationsAsRead = () => {
    if (showBadge) {
      const notificationItemsIds = groupedNotification.map(
        (subItem) => subItem.notification_id
      );
      markNotificationsRead({ notification_ids: notificationItemsIds });
    }
  };

  const notificationColorToDisplayColor = {
    [NotificationColor.BLUE]: theme.colors.primaryBlue,
    [NotificationColor.GREEN]: theme.colors.green,
    [NotificationColor.RED]: theme.colors.red,
    [NotificationColor.YELLOW]: theme.colors.gold,
  };

  const getTimelineItemColor = (notification: Notification) =>
    notification.color
      ? notificationColorToDisplayColor[notification.color]
      : notificationColorToDisplayColor[NotificationColor.BLUE];

  const getSingleNotificationSubMessage = () => {
    let subMessage = lastNotificationItem?.sub_message;
    if (!isLongNotification) {
      return {
        subMessage,
        collapsedSubMessage: undefined,
      };
    }
    if (subMessage?.charAt(maxNotificationLength) === " ") {
      return {
        subMessage: addTrailingDots(
          subMessage?.slice(0, maxNotificationLength),
          !expandedSingleNotification
        ),
        collapsedSubMessage: subMessage.slice(maxNotificationLength),
      };
    }
    for (let i = maxNotificationLength - 1; i >= 0; i--) {
      if (subMessage?.charAt(i) === " ") {
        return {
          subMessage: addTrailingDots(
            subMessage?.slice(0, i),
            !expandedSingleNotification
          ),
          collapsedSubMessage: subMessage.slice(i + 1),
        };
      }
    }
  };

  return {
    lastNotificationItem,
    isAggregateNotification,
    showBadge,
    clickable,
    showButton,
    buttonText,
    markUnreadNotificationsAsRead,
    delayedExpandedTimeline,
    getTimelineItemColor,
    isLongNotification,
    getSingleNotificationSubMessage,
    isCollapsible,
  };
};

export default useNotification;
